/**
 * Activity-first Flood Rescue Bridge — all nine phases.
 * Presentation groups reference existing authored block IDs and saved fields.
 * No answer keys or synthetic measurements are introduced here.
 */
export const FLOOD_RESCUE_STEPS = [
  "flood-rescue-prelude", "rescue-prediction", "flat-bridge-test",
  "hidden-forces", "engineer-bridge-two", "fair-rescue-test",
  "young-engineer-report", "evidence-reflection", "next-variable-mission",
];

export const FLOOD_RESCUE_FLOW = {
  "flood-rescue-prelude": [
    { id: "briefing", title: "Answer the rescue call", purpose: "Discover what Green Valley needs and what materials are allowed.", blockIds: ["flood-rescue-story-prelude"] },
    { id: "facts", title: "Identify the mission facts", purpose: "Find a fact from the briefing before proposing a solution.", blockIds: ["mission-fact-check"] },
  ],
  "rescue-prediction": [
    { id: "predict", title: "Make your first prediction", purpose: "Look at flat paper. Predict how much it might hold and explain why—before testing.", blockIds: ["wonder-story", "bridge1-capacity-prediction"] },
  ],
  "flat-bridge-test": [
    { id: "setup", title: "Set up your flat bridge", purpose: "Use stable supports and a flat sheet. Check the real setup before loading it.", blockIds: ["explore-story", "baseline-checklist"] },
    { id: "test", title: "Test and record Bridge 1", purpose: "Add one object at a time, observe what happens, and record the actual result.", blockIds: ["bridge1-record", "bridge1-observation"] },
  ],
  "hidden-forces": [
    { id: "forces", title: "Find the forces", purpose: "Use your first test and the short video to identify the upward and downward forces.", blockIds: ["bridge1-prior-evidence", "invisible-forces-video", "force-check"], prerequisites: [{ stepId: "flat-bridge-test", field: "bridge1_record", title: "Bridge 1 test result" }] },
    { id: "explain", title: "Explain why Bridge 1 bent", purpose: "Explore why shape matters, then explain your own observation using science.", blockIds: ["same-paper-shape-video", "bridge1-science-explanation"] },
  ],
  "engineer-bridge-two": [
    { id: "change", title: "Choose one design change", purpose: "Use what you learned to plan a better bridge. Change only the bridge design.", blockIds: ["apply-story", "design-change"] },
    { id: "predict", title: "Predict how your change will work", purpose: "Explain what you expect to happen and why, before you build or test.", blockIds: ["design-prediction"] },
  ],
  "fair-rescue-test": [
    { id: "evidence", title: "Find your starting evidence", purpose: "Recall what happened to Bridge 1 and what you changed in Bridge 2.", blockIds: ["trial1-summary"], prerequisites: [
      { stepId: "flat-bridge-test", field: "bridge1_record", title: "Bridge 1 result" },
      { stepId: "engineer-bridge-two", field: "bridge2_design_change", title: "Bridge 2 design plan" },
    ] },
    { id: "fair", title: "Prepare a fair comparison", purpose: "Watch the fair-test video and physically check that your setup is consistent.", blockIds: ["fair-test-video", "fair-test-checklist"] },
    { id: "test", title: "Test and record Bridge 2", purpose: "Test your redesign and record what actually happened—even if it did worse.", blockIds: ["bridge2-record"] },
    { id: "compare", title: "Compare the two results", purpose: "Use the measurements and one observation to decide what the evidence supports.", blockIds: ["comparison-claim"] },
  ],
  "young-engineer-report": [
    { id: "review", title: "Open your rescue evidence", purpose: "Look at both tests and how engineers make a claim using evidence and reasoning.", blockIds: ["failure-clues-video", "report-prior-work"], prerequisites: [
      { stepId: "flat-bridge-test", field: "bridge1_record", title: "Bridge 1 result" },
      { stepId: "fair-rescue-test", field: "bridge2_record", title: "Bridge 2 result" },
    ] },
    { id: "first", title: "Why did Bridge 1 bend?", purpose: "Connect your direct observation to forces. Don't just repeat its result.", blockIds: ["first-failure-explanation"] },
    { id: "design", title: "Why did you change the design?", purpose: "Explain why you made your specific engineering choice.", blockIds: ["change-explanation"] },
    { id: "defend", title: "Defend your engineering claim", purpose: "Use the measurements from both tests to defend a conclusion, including uncertainty.", blockIds: ["evidence-defense"] },
  ],
  "evidence-reflection": [
    { id: "thinking", title: "How did your thinking change?", purpose: "Compare your first prediction with what actually happened.", blockIds: ["reflection-prior-work", "thinking-change"], prerequisites: [
      { stepId: "rescue-prediction", field: "bridge1_prediction", title: "Opening prediction" },
    ] },
    { id: "failure", title: "What did failure teach you?", purpose: "Describe something you learned even if a bridge didn't perform as expected.", blockIds: ["failure-learning"] },
  ],
  "next-variable-mission": [
    { id: "real", title: "Connect your bridge to real engineering", purpose: "Watch how the same thinking applies to actual bridges.", blockIds: ["real-bridges-video"] },
    { id: "next", title: "Invent your next fair test", purpose: "Pick one variable, explain what stays the same, and decide what to measure.", blockIds: ["next-variable-choice", "next-test-plan"] },
  ],
};

const hasValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.values(value).some(hasValue);
  return true;
};
const hasBridgeRecord = (value) =>
  !!value && hasValue(value.capacity) && hasValue(value.failure_location) &&
  String(value.failure_observation ?? value.observation ?? "").trim().length >= 8;

export const floodRescuePrerequisiteComplete = (item, sessionState) => {
  const step = sessionState?.steps?.[item.stepId] || {};
  const value = step[item.field];
  if (item.field === "bridge1_record" || item.field === "bridge2_record") return hasBridgeRecord(value);
  if (item.field === "bridge2_design_change") {
    return String(value || "").trim().length >= 8 &&
      String(step.bridge2_design_prediction || "").trim().length >= 15;
  }
  return hasValue(value);
};

export const getFloodRescueActivityState = (stepId, sessionState, completionState) => {
  const specs = FLOOD_RESCUE_FLOW[stepId] || [];
  const items = completionState?.items || [];
  const activities = specs.map((spec) => {
    const applicable = items.filter((item) => spec.blockIds.includes(item.targetBlockId));
    const missing = (spec.prerequisites || []).filter((item) =>
      !floodRescuePrerequisiteComplete(item, sessionState));
    return {
      ...spec,
      missing,
      missingLabel: missing[0]?.title || applicable.find((item) => !item.complete)?.label || "",
      complete: missing.length === 0 && applicable.every((item) => item.complete),
      required: applicable.length,
    };
  });
  const firstIncomplete = activities.findIndex((item) => !item.complete);
  return {
    activities,
    maxUnlockedIndex: firstIncomplete < 0 ? Math.max(0, activities.length - 1) : firstIncomplete,
    allComplete: firstIncomplete < 0,
  };
};

// A single opt-in enables both Professor Swan and the activity-first flow.
// Keep the ordinary Saras-3D/teacher-led experience unchanged without it.
export const isFloodRescueWholeJourneyEnabled = () => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("ff_swan_unified_panel") === "1" ||
      /^\/wonderlabs(?:\/|$)/.test(window.location.pathname);
  } catch (_error) { return false; }
};
